import { DatePicker } from '@/app/common/components/datepicker'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { useUpdateProgramAcademicTerm } from '@/queries/academic_terms/mutations'
import { ProgramTermsArray } from '@/queries/academic_terms/schema'
import { ProgramYearsProps } from '@/queries/program/schemas'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shad/ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { addDays, format } from 'date-fns'
import { useForm } from 'react-hook-form'
import z from 'zod'
import { AwardDatesSchema } from './select-award-dates.schema'

function parseYYYYMMDDAsLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day) // month is 0-indexed
}

export type AwardDatesOptions = z.infer<ReturnType<typeof AwardDatesSchema>>

type Props = {
  selectedAwardPeriodId: number
  onOpenChange: (open: boolean) => void
  programAcademicTerms: ProgramTermsArray
  programSettings: ProgramYearsProps
}

export default function SetTermStartAndEndDateForm(props: Props) {
  const {
    selectedAwardPeriodId,
    onOpenChange,
    programSettings,
    programAcademicTerms,
  } = props

  const { mutate: updateProgramAcademicTerm, status } =
    useUpdateProgramAcademicTerm(programSettings.id)

  function onAwardDatesSubmit(data: AwardDatesOptions) {
    updateProgramAcademicTerm(
      {
        programId: programSettings.id,
        academicTermId: selectedAwardPeriodId,
        startDate: format(data.startDate, 'yyyy-MM-dd'),
        endDate: format(data.endDate, 'yyyy-MM-dd'),
        schoolYear: programSettings.period,
      },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      }
    )
  }

  const selectedAwardPeriod = programAcademicTerms.find(
    (period) => period.id === selectedAwardPeriodId
  )

  function getDefaultValues() {
    if (
      selectedAwardPeriod?.awarding_period_start_date &&
      selectedAwardPeriod?.awarding_period_end_date
    ) {
      return {
        startDate: new Date(selectedAwardPeriod.awarding_period_start_date),
        endDate: new Date(selectedAwardPeriod.awarding_period_end_date),
      }
    }

    return {
      startDate: getMinDate(),
      endDate: getMaxDate(),
    }
  }

  function getMinDate() {
    return parseYYYYMMDDAsLocalDate(programSettings.program_start_date)
  }

  function getMaxDate() {
    return parseYYYYMMDDAsLocalDate(programSettings.program_end_date)
  }

  function getEndDateMinDate() {
    const today = new Date()
    const tomorrow = addDays(today, 1)
    return tomorrow
  }

  const awardDatesForm = useForm<AwardDatesOptions>({
    resolver: zodResolver(
      AwardDatesSchema(programAcademicTerms, selectedAwardPeriodId)
    ),
    defaultValues: getDefaultValues(),
  })

  const hasAddedDates =
    selectedAwardPeriod?.awarding_period_start_date &&
    selectedAwardPeriod?.awarding_period_end_date

  return (
    <Dialog open={true} onOpenChange={onOpenChange} key={selectedAwardPeriodId}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {hasAddedDates ? 'Edit' : 'Add'} {selectedAwardPeriod?.name} Award
            Dates
          </DialogTitle>
          <p className="text-gray-600 text-sm">
            Set the start and end dates for when awards (scholarships, grants,
            financial aid) can be distributed during this term
          </p>
        </DialogHeader>
        <Form {...awardDatesForm}>
          <form
            className="space-y-4 "
            onSubmit={awardDatesForm.handleSubmit(onAwardDatesSubmit)}
          >
            <FormField
              name="startDate"
              control={awardDatesForm.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Date</FormLabel>
                  <FormControl>
                    <DatePicker
                      side="top"
                      minDate={new Date('2015-01-01')}
                      maxDate={getMaxDate()}
                      name="startDate"
                      initialDate={new Date(field.value)}
                      onDateChange={(date) => {
                        field.onChange(date)
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="endDate"
              control={awardDatesForm.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End Date</FormLabel>
                  <FormControl>
                    <DatePicker
                      name="endDate"
                      side="top"
                      initialDate={new Date(field.value)}
                      minDate={getEndDateMinDate()}
                      maxDate={getMaxDate()}
                      onDateChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="pt-10">
              <SubmitButton
                status={status}
                size={'lg'}
                type="submit"
                buttonTxt="Save Award Dates"
              />
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
